<template>
  <div>
    <transition-group name="list" tag="div">
      <div v-for="(location, index) in managedLocations" :key="index" class="d-flex align-items-center gap-2 mb-2">
        <div class="location-combobox-wrapper">
          <LeadCombobox
            :model-value="location"
            :items="options"
            :getOptionLabel="(option) => option.label"
            @update:model-value="updateLocation(index, $event)"
            @search="onSearch"
            :loading="isLoading"
            :disable-filtering="true"
            placeholder="e.g. Zurich, Switzerland">
          </LeadCombobox>
        </div>
        <LeadButton variant="outline-danger" class="location-remove-button" @click="removeLocation(index)">
          <template #icon>
            <IconTrashFilled size="16" />
          </template>
        </LeadButton>
      </div>
    </transition-group>
  </div>

  <div>
    <LeadButton variant="secondary" @click="addLocationEntry">
      {{ $t('btn.add_location') }}
    </LeadButton>
  </div>

  <div v-if="props.errors && Object.keys(props.errors).length > 0">
    <div v-for="(errorList, field) in props.errors" :key="field" class="mt-2">
      <FormErrorList :errors="errorList" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconTrashFilled } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import { request } from '@/api/client';
import type { LocationItem } from '@/components/orders/details/provider_forms/AdformForm.vue';
import LeadCombobox from './LeadCombobox.vue';

interface IResponseLocation {
  cityId?: number;
  countryId: number;
  fullName: string;
  name: string;
  regionId?: number;
  type: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: LocationItem[];
    providerId: string | number;
    errors?: Record<string, string[]>;
    default?: LocationItem;
  }>(),
  {
    modelValue: () => [],
    errors: () => ({}),
    default: () => ({ label: '', id: 0, type: '' }),
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: LocationItem[]];
}>();

const options = ref<LocationItem[]>([]);
const isLoading = ref(false);

const managedLocations = computed({
  get: () => {
    // Ensure we always have at least one entry for the UI
    if (!props.modelValue || props.modelValue.length === 0) {
      return [
        {
          label: 'Please select a location...',
          id: 0,
          type: '',
        },
      ];
    }
    return props.modelValue;
  },
  set: (value: LocationItem[]) => {
    emit('update:modelValue', value);
  },
});

// --- METHODS ---
const addLocationEntry = () => {
  const newPlaceholder: LocationItem = props.default;
  const newList = [...managedLocations.value, newPlaceholder];
  managedLocations.value = newList;
};

const onSearch = async (search: string) => {
  if (search.length < 3) {
    options.value = [];
    return;
  }
  isLoading.value = true;
  try {
    request('GET', `/v1/account/campaigns/${props.providerId}/options/target_locations`, { query: search })
      .then((response: { data: IResponseLocation[] }) => {
        options.value = response.data.map((item) => {
          if (item.type === 'city' && item.cityId) return { id: item.cityId, label: item.fullName, type: 'city' };
          if (item.type === 'region' && item.regionId)
            return { id: item.regionId, label: item.fullName, type: 'region' };
          return { id: item.countryId, label: item.fullName, type: 'country' };
        });
      })
      .finally(() => {
        isLoading.value = false;
      });
  } catch (error) {
    console.error('Error fetching locations:', error);
    isLoading.value = false;
  }
};

/**
 * @param index The index of the item to update.
 * @param newLocation The new location object from the GeoLocationSearch component.
 */
const updateLocation = (index: number, newLocation: LocationItem) => {
  const newList = [...managedLocations.value];
  newList[index] = newLocation;
  managedLocations.value = newList;
};

/**
 * @param index The index of the item to remove.
 */
const removeLocation = (index: number) => {
  const newList = [...managedLocations.value];
  newList.splice(index, 1);

  managedLocations.value = newList;
};
</script>

<style>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.location-combobox-wrapper {
  flex: 1 1 auto;
  min-width: 0;
}

.location-remove-button {
  flex-shrink: 0;
}
</style>
